import { useEffect, useRef, useState } from 'react';
import { useAppDispatch } from '../../../../../hooks/redux';
import {
  addScheduleMatchesToSelection,
  setScheduleSelection,
} from '../../../../../features/schedule/scheduleSlice';
import { collectMatchIdsInMarquee } from '../../../../../features/schedule/scheduleBulkSelectionHandlers';
import './ScheduleSelectionMarquee.scss';

type MarqueeRect = { left: number; top: number; width: number; height: number };

export type ScheduleSelectionMarqueeProps = {
  disabled?: boolean;
  containerRef: React.RefObject<HTMLElement | null>;
};

export function ScheduleSelectionMarquee({
  disabled = false,
  containerRef,
}: ScheduleSelectionMarqueeProps) {
  const dispatch = useAppDispatch();
  const originRef = useRef<{ x: number; y: number } | null>(null);
  const unionSelectionRef = useRef(false);
  const [marquee, setMarquee] = useState<MarqueeRect | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || disabled) return;

    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 0 || e.ctrlKey || e.metaKey) return;
      if (!container.contains(e.target as Node)) return;

      const onMatch = !!(e.target as HTMLElement).closest('[data-match-id]');
      if (onMatch && !e.shiftKey) return;

      e.preventDefault();
      e.stopPropagation();

      originRef.current = { x: e.clientX, y: e.clientY };
      unionSelectionRef.current = e.shiftKey;
      document.body.style.userSelect = 'none';
      setMarquee({ left: e.clientX, top: e.clientY, width: 0, height: 0 });
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!originRef.current) return;
      const x = e.clientX;
      const y = e.clientY;
      setMarquee({
        left: Math.min(originRef.current.x, x),
        top: Math.min(originRef.current.y, y),
        width: Math.abs(x - originRef.current.x),
        height: Math.abs(y - originRef.current.y),
      });
    };

    const finish = (e: PointerEvent) => {
      document.body.style.userSelect = '';

      if (!originRef.current || !containerRef.current) {
        originRef.current = null;
        setMarquee(null);
        return;
      }

      const left = Math.min(originRef.current.x, e.clientX);
      const top = Math.min(originRef.current.y, e.clientY);
      const right = Math.max(originRef.current.x, e.clientX);
      const bottom = Math.max(originRef.current.y, e.clientY);

      if (right - left > 4 && bottom - top > 4) {
        const ids = collectMatchIdsInMarquee(containerRef.current, { left, top, right, bottom });
        if (ids.length > 0) {
          if (unionSelectionRef.current) {
            dispatch(addScheduleMatchesToSelection(ids));
          } else {
            dispatch(setScheduleSelection(ids));
          }
        }
      }

      originRef.current = null;
      unionSelectionRef.current = false;
      setMarquee(null);
    };

    const onSelectStart = (e: Event) => {
      if (originRef.current) e.preventDefault();
    };

    window.addEventListener('pointerdown', onPointerDown, true);
    container.addEventListener('selectstart', onSelectStart);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', finish);
    window.addEventListener('pointercancel', finish);

    return () => {
      document.body.style.userSelect = '';
      window.removeEventListener('pointerdown', onPointerDown, true);
      container.removeEventListener('selectstart', onSelectStart);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', finish);
      window.removeEventListener('pointercancel', finish);
    };
  }, [containerRef, disabled, dispatch]);

  if (!marquee || marquee.width <= 0 || marquee.height <= 0) return null;

  return (
    <div
      className="schedule-selection-marquee-rect"
      style={{
        left: marquee.left,
        top: marquee.top,
        width: marquee.width,
        height: marquee.height,
      }}
    />
  );
}
